/**
 * FTR5 / FTR5b: keyboard + mouse input.
 *
 * The semantic outputs are unchanged; the keymap now comes from the settings
 * store instead of being hardcoded in this file.
 */
import { actionsForKey, normalizeBindingKey } from './keymap.js';
import type { BindingAction } from './keymap.js';
import { getSettings } from '../settings/store.js';

export interface InputState {
  throttle: number;
  strafe: number;
  vertical: number;
  roll: number;
  keyYaw: number;
  keyPitch: number;
  mouseDX: number;
  mouseDY: number;
  toggleAssist: boolean;
  toggleRotAssist: boolean;
  boost: boolean;
  pipSys: boolean;
  pipEng: boolean;
  pipWep: boolean;
  toggleCamera: boolean;
  targetNext: boolean;
  targetPrev: boolean;
  toggleTargetMenu: boolean;
  targetSignal: boolean;
  toggleSupercruise: boolean;
  toggleLegend: boolean;
  dockRequest: boolean;
  fire: boolean;
  scoop: boolean;
  verify: boolean;
  toggleOrbitLines: boolean;
  lookReleased: boolean;
}

const THROTTLE_RAMP = 0.9;

export class Input {
  readonly state: InputState = {
    throttle: 0,
    strafe: 0,
    vertical: 0,
    roll: 0,
    keyYaw: 0,
    keyPitch: 0,
    mouseDX: 0,
    mouseDY: 0,
    toggleAssist: false,
    toggleRotAssist: false,
    boost: false,
    pipSys: false,
    pipEng: false,
    pipWep: false,
    toggleCamera: false,
    targetNext: false,
    targetPrev: false,
    toggleTargetMenu: false,
    targetSignal: false,
    toggleSupercruise: false,
    toggleLegend: false,
    dockRequest: false,
    fire: false,
    scoop: false,
    verify: false,
    toggleOrbitLines: false,
    lookReleased: false,
  };

  private menuOpenFn: () => boolean = () => false;
  private readonly keys = new Set<string>();
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private dragMoved = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', (event) => this.onKey(event, true));
    window.addEventListener('keyup', (event) => this.onKey(event, false));
    canvas.addEventListener('mousedown', (event) => this.startDrag(event));
    window.addEventListener('mouseup', () => this.endDrag());
    document.addEventListener('pointerlockchange', () => this.onPointerLockChange());
    window.addEventListener('mousemove', (event) => this.onMouseMove(event));
  }

  setMenuOpenPredicate(fn: () => boolean): void {
    this.menuOpenFn = fn;
  }

  zeroThrottle(): void {
    this.state.throttle = 0;
  }

  tickThrottle(dt: number): void {
    const up = this.isPressed('throttleUp');
    const down = this.isPressed('throttleDown');
    if (up && !down) this.setThrottle(this.state.throttle + THROTTLE_RAMP * dt);
    else if (down && !up) this.setThrottle(this.state.throttle - THROTTLE_RAMP * dt);
  }

  consume(): InputState {
    const boostHeld = this.boostMode() === 'hold' && this.isPressed('boost');
    const snap: InputState = { ...this.state, boost: this.state.boost || boostHeld };
    this.resetEdges();
    return snap;
  }

  private onKey(event: KeyboardEvent, down: boolean): void {
    const key = normalizeBindingKey(event.key);
    const wasDown = this.keys.has(key);
    this.preventArrowScroll(key, event);
    this.handleThrottleCut(key, down);
    this.handleBindings(key, down, wasDown, event);
    this.handleUtilityKeys(key, down, wasDown, event);
    if (down) this.keys.add(key);
    else this.keys.delete(key);
    this.recompute();
  }

  private handleBindings(
    key: string,
    down: boolean,
    wasDown: boolean,
    event: KeyboardEvent,
  ): void {
    if (!down && !wasDown) return;
    const actions = this.filteredActions(key, event.shiftKey);
    if (actions.includes('toggleTargetMenu')) event.preventDefault();
    if (down && !wasDown) this.applyDownActions(actions);
    if (!down && wasDown) this.applyUpActions(actions);
  }

  private applyDownActions(actions: BindingAction[]): void {
    if (actions.includes('toggleAssist')) this.handleAssistEdge();
    if (actions.includes('toggleRotAssist')) this.state.toggleRotAssist = true;
    if (actions.includes('boost') && this.boostMode() === 'toggle') this.state.boost = true;
    if (actions.includes('fire')) this.state.fire = true;
    if (actions.includes('toggleCamera')) this.state.toggleCamera = true;
    if (actions.includes('toggleTargetMenu')) this.state.toggleTargetMenu = true;
    if (actions.includes('targetSignal')) this.state.targetSignal = true;
    if (actions.includes('toggleSupercruise')) this.state.toggleSupercruise = true;
    if (actions.includes('dockRequest')) this.state.dockRequest = true;
    if (actions.includes('scoop')) this.state.scoop = true;
    if (actions.includes('verify')) this.state.verify = true;
    if (actions.includes('toggleOrbitLines')) this.state.toggleOrbitLines = true;
  }

  private applyUpActions(actions: BindingAction[]): void {
    if (actions.includes('toggleAssist') && this.assistMode() === 'hold') {
      this.state.toggleAssist = true;
    }
  }

  private handleAssistEdge(): void {
    this.state.toggleAssist = true;
  }

  private handleUtilityKeys(
    key: string,
    down: boolean,
    wasDown: boolean,
    event: KeyboardEvent,
  ): void {
    if (!down || wasDown) return;
    if (key === '1') this.state.pipSys = true;
    if (key === '2') this.state.pipEng = true;
    if (key === '3') this.state.pipWep = true;
    if (key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      this.state.targetPrev = true;
    }
    if (key === 'h') this.state.toggleLegend = true;
  }

  private handleThrottleCut(key: string, down: boolean): void {
    const matches = actionsForKey(key, getSettings().controls.keymap);
    if (down && matches.includes('throttleCut')) this.state.throttle = 0;
  }

  private preventArrowScroll(key: string, event: KeyboardEvent): void {
    if (key.startsWith('Arrow')) event.preventDefault();
  }

  private startDrag(event: MouseEvent): void {
    this.dragging = true;
    this.dragMoved = false;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
  }

  private endDrag(): void {
    if (!this.dragMoved && document.pointerLockElement !== this.canvas && !this.menuOpenFn()) {
      this.requestPointerLock();
    }
    this.dragging = false;
  }

  private requestPointerLock(): void {
    try {
      const result = this.canvas.requestPointerLock();
      if (result && typeof (result as Promise<void>).catch === 'function') {
        (result as Promise<void>).catch(() => undefined);
      }
    } catch {
      /* drag look remains available */
    }
  }

  private onPointerLockChange(): void {
    if (document.pointerLockElement !== this.canvas) this.state.lookReleased = true;
  }

  private onMouseMove(event: MouseEvent): void {
    if (document.pointerLockElement === this.canvas) {
      this.state.mouseDX += event.movementX;
      this.state.mouseDY += event.movementY;
      return;
    }
    if (!this.dragging) return;
    const dx = event.clientX - this.lastX;
    const dy = event.clientY - this.lastY;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    if (dx !== 0 || dy !== 0) this.dragMoved = true;
    this.state.mouseDX += dx;
    this.state.mouseDY += dy;
  }

  private setThrottle(value: number): void {
    this.state.throttle = Math.max(-1, Math.min(1, value));
  }

  private axis(positive: BindingAction, negative: BindingAction): number {
    return (this.isPressed(positive) ? 1 : 0) - (this.isPressed(negative) ? 1 : 0);
  }

  private isPressed(action: BindingAction): boolean {
    const key = getSettings().controls.keymap[action];
    return key ? this.keys.has(key) : false;
  }

  private filteredActions(key: string, shiftKey: boolean): BindingAction[] {
    const actions = actionsForKey(key, getSettings().controls.keymap);
    if (key !== 'Tab' || !shiftKey) return actions;
    return actions.filter((action) => action !== 'toggleTargetMenu');
  }

  private recompute(): void {
    this.state.strafe = this.axis('strafeRight', 'strafeLeft');
    this.state.vertical = this.axis('verticalUp', 'verticalDown');
    this.state.roll = this.axis('rollRight', 'rollLeft');
    this.state.keyYaw = this.axis('yawRight', 'yawLeft');
    this.state.keyPitch = this.axis('pitchDown', 'pitchUp');
  }

  private resetEdges(): void {
    this.state.mouseDX = 0;
    this.state.mouseDY = 0;
    this.state.toggleAssist = false;
    this.state.toggleRotAssist = false;
    this.state.boost = false;
    this.state.pipSys = false;
    this.state.pipEng = false;
    this.state.pipWep = false;
    this.state.toggleCamera = false;
    this.state.targetNext = false;
    this.state.targetPrev = false;
    this.state.toggleTargetMenu = false;
    this.state.targetSignal = false;
    this.state.toggleSupercruise = false;
    this.state.toggleLegend = false;
    this.state.dockRequest = false;
    this.state.fire = false;
    this.state.scoop = false;
    this.state.verify = false;
    this.state.toggleOrbitLines = false;
    this.state.lookReleased = false;
  }

  private assistMode(): 'toggle' | 'hold' {
    return getSettings().controls.assistMode;
  }

  private boostMode(): 'toggle' | 'hold' {
    return getSettings().controls.boostMode;
  }
}
